import { AppearanceOverlay } from "src/types/experiences/storyboardSettings";
import { BackgroundProperty } from "src/types/experiences/storyboardSettings/primitives";
import {
  STORYBOARD_TYPES,
  StoryboardType,
} from "src/types/experiences/storyboardSettings/storyboard";
import { WorkspaceSubscription } from "src/types/experiences/workspaces";

export const SEGMENT_STORYBOARDS = new Set<StoryboardType>([
  STORYBOARD_TYPES.LANDING_PAGE,
  STORYBOARD_TYPES.RESOURCE_CENTER,
]);

export const INTERACTIVE_CONTENT_TYPES = new Set<StoryboardType>([
  STORYBOARD_TYPES.INTERACTIVE_CONTENT,
  STORYBOARD_TYPES.INTERACTIVE_CONTENT_V2,
]);

export const BLANK_TEMPLATE_IDS: Partial<Record<StoryboardType, string>> = {
  [STORYBOARD_TYPES.INTERACTIVE_CONTENT_V2]: "blank_interactive_template_v2",
  [STORYBOARD_TYPES.LANDING_PAGE]: "blank_landing_page_template",
  [STORYBOARD_TYPES.RESOURCE_CENTER]: "blank_resource_center_template",
  [STORYBOARD_TYPES.SPATIAL]: "blank_spatial_template",
  [STORYBOARD_TYPES.FLIP_BOOK]: "blank_flipbook_template",
};

export const PPT_IMPORT_CONTENT_TYPES: StoryboardType[] = [
  STORYBOARD_TYPES.INTERACTIVE_CONTENT_V2,
  STORYBOARD_TYPES.SPATIAL,
];

export const CONTENT_TYPE_SUBSCRIPTION_KEY: Partial<
  Record<StoryboardType, keyof WorkspaceSubscription>
> = {
  [STORYBOARD_TYPES.INTERACTIVE_CONTENT_V2]: "interactiveContentEnabled",
  [STORYBOARD_TYPES.LANDING_PAGE]: "landingPageEnabled",
  [STORYBOARD_TYPES.RESOURCE_CENTER]: "resourceCenterEnabled",
  [STORYBOARD_TYPES.SPATIAL]: "spatialEnabled",
  [STORYBOARD_TYPES.FLIP_BOOK]: "flipBookEnabled",
};

// cleverstory default: a missing subscription means the flag is false (hidden).
export const getAllowedContentTypes = (
  subscription: WorkspaceSubscription | null | undefined,
  candidates: StoryboardType[],
): StoryboardType[] =>
  candidates.filter((type) => {
    const key = CONTENT_TYPE_SUBSCRIPTION_KEY[type];
    return key ? subscription?.[key] === true : false;
  });

// Decides whether an existing storyboard of a given type should be shown for the
// active workspace subscription. Used to filter the storyboards list so disabled
// content types never surface. Strict: an unknown type, or a type with no
// subscription flag, is hidden. Legacy INTERACTIVE_CONTENT (v1) is normalized to
// the same flag as v2. While the subscription is still loading we don't filter,
// to avoid flashing an empty list.
export const isStoryboardTypeEnabled = (
  storyboardType: string | null | undefined,
  subscription: WorkspaceSubscription | null | undefined,
): boolean => {
  if (!subscription) return true;
  if (!storyboardType) return false;
  const normalized = INTERACTIVE_CONTENT_TYPES.has(storyboardType as StoryboardType)
    ? STORYBOARD_TYPES.INTERACTIVE_CONTENT_V2
    : (storyboardType as StoryboardType);
  const key = CONTENT_TYPE_SUBSCRIPTION_KEY[normalized];
  return key ? subscription[key] === true : false;
};

export const TEMPLATE_SORT_FIELDS = {
  CREATED_DATE: "created_date",
  NAME: "Name",
} as const;

export const TEMPLATE_SORT_ORDERS = {
  ASC: "ASC",
  DESC: "DESC",
} as const;

export type TemplateSortField =
  (typeof TEMPLATE_SORT_FIELDS)[keyof typeof TEMPLATE_SORT_FIELDS];
export type TemplateSortOrder =
  (typeof TEMPLATE_SORT_ORDERS)[keyof typeof TEMPLATE_SORT_ORDERS];

export const TEMPLATE_SORT_OPTIONS: {
  label: string;
  value: TemplateSortField;
}[] = [
  { label: "Created date", value: TEMPLATE_SORT_FIELDS.CREATED_DATE },
  { label: "Name", value: TEMPLATE_SORT_FIELDS.NAME },
];

export const STORYBOARD_CREATED = 'STORYBOARD_CREATED';

export const ALL_FILTER_VALUE = "";
export const INDUSTRY_ALL_LABEL = "All Industry";
export const CATEGORY_ALL_LABEL = "All Templates";

/** SITE SETTINGS */
export const OVERLAY_OPTIONS: { value: AppearanceOverlay; label: string }[] = [
  { value: "none", label: "None" },
  { value: "light", label: "Light" },
  { value: "dark", label: "Dark" },
];

export const DEFAULT_BACKGROUND: BackgroundProperty = {
  type: "color",
  color: null,
  media: null,
  opacity: 100,
  scale: "",
  position: "",
  blur: 0,
};

export const SUPPORTED_TYPES = [
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/svg+xml",
  "image/webp",
];

export const ROLE_OPTIONS = [
  { key: "owner", label: "Owner" },
  { key: "editor", label: "Editor" },
  { key: "viewer", label: "Viewer" },
];

export const inviteUserHeader = [
  { label: "name", width: 9, value: "name" },
  { label: "role", width: 3, value: "role" },
];

export const PAGE_WIDGET_TYPE = "cleverstory.v2.widgets.page";